export const VERSION = '1.0.0';
export const MAP_SIZES = Object.freeze({
  frontier: Object.freeze({ id: 'frontier', name: 'Frontier Basin', width: 1600, height: 1000, camps: 3, resourceScale: 0.8 }),
  expanse: Object.freeze({ id: 'expanse', name: 'Shardmeadow Expanse', width: 2400, height: 1500, camps: 5, resourceScale: 1.25 }),
  dominion: Object.freeze({ id: 'dominion', name: 'Dominion Reach', width: 3200, height: 2000, camps: 7, resourceScale: 1.7 }),
});
export const DEFAULT_MAP_SIZE = 'expanse';
export const WORLD = MAP_SIZES[DEFAULT_MAP_SIZE];
export const RESOURCE_TYPES = Object.freeze(['rations', 'timber', 'stone', 'lumen']);

export const AGES = Object.freeze({
  1: Object.freeze({ id: 'hearthrise', name: 'Hearthrise Age', researchTime: 0, cost: Object.freeze({}), unlocks: Object.freeze(['worker', 'melee', 'depot', 'barracks']) }),
  2: Object.freeze({ id: 'waystone', name: 'Waystone Age', researchTime: 32, cost: Object.freeze({ rations: 180, timber: 140, stone: 100, lumen: 18 }), unlocks: Object.freeze(['ranged', 'tower']) }),
  3: Object.freeze({ id: 'lumenwake', name: 'Lumenwake Age', researchTime: 48, cost: Object.freeze({ rations: 320, timber: 250, stone: 210, lumen: 45 }), unlocks: Object.freeze(['veteran-armies', 'masterworks']) }),
});

export const RESEARCH = Object.freeze({
  'master-tools': Object.freeze({ id: 'master-tools', name: 'Master Tools', age: 2, source: 'depot', researchTime: 24, cost: Object.freeze({ rations: 90, timber: 120, stone: 55 }), effects: Object.freeze({ gatherRate: 1.15, buildSpeed: 1.1, repairRate: 1.15 }) }),
  'field-armor': Object.freeze({ id: 'field-armor', name: 'Field Armor', age: 2, source: 'barracks', researchTime: 28, cost: Object.freeze({ rations: 130, timber: 75, stone: 95 }), effects: Object.freeze({ unitHp: 1.15, unitArmor: 2 }) }),
  'honed-arms': Object.freeze({ id: 'honed-arms', name: 'Honed Arms', age: 2, source: 'barracks', researchTime: 26, cost: Object.freeze({ rations: 145, timber: 105, lumen: 10 }), effects: Object.freeze({ unitDamage: 1.12 }) }),
  'fortified-masonry': Object.freeze({ id: 'fortified-masonry', name: 'Fortified Masonry', age: 2, source: 'heart', researchTime: 30, cost: Object.freeze({ timber: 135, stone: 165, lumen: 12 }), effects: Object.freeze({ buildingHp: 1.2, buildingArmor: 2 }) }),
  'lumen-ballistics': Object.freeze({ id: 'lumen-ballistics', name: 'Lumen Ballistics', age: 3, source: 'tower', researchTime: 35, cost: Object.freeze({ timber: 125, stone: 150, lumen: 35 }), effects: Object.freeze({ towerDamage: 1.25, towerRange: 1.12 }) }),
  'heroic-discipline': Object.freeze({ id: 'heroic-discipline', name: 'Heroic Discipline', age: 3, source: 'heart', researchTime: 38, cost: Object.freeze({ rations: 210, stone: 120, lumen: 42 }), effects: Object.freeze({ heroCooldown: 0.82, captureRate: 1.12 }) }),
});

export const FORTIFICATION_LEVELS = Object.freeze({
  1: Object.freeze({ level: 1, name: 'Palisade Mantle', age: 2, hpMultiplier: 1.2, armor: 2, cost: Object.freeze({ timber: 100, stone: 120 }) }),
  2: Object.freeze({ level: 2, name: 'Lumen Bulwark', age: 3, hpMultiplier: 1.2, armor: 2, cost: Object.freeze({ timber: 160, stone: 220, lumen: 25 }) }),
});

// Simulation-facing tuning kept in data so orders, UI hints, and AI all share
// one deterministic ruleset.
export const WORKER_JOBS = Object.freeze({
  idle: Object.freeze({ label: 'Awaiting orders' }),
  travel: Object.freeze({ label: 'Traveling' }),
  gather: Object.freeze({ label: 'Gathering', interactionRange: 3, maxSlots: 4 }),
  build: Object.freeze({ label: 'Constructing', interactionRange: 3, maxSlots: 8 }),
  repair: Object.freeze({ label: 'Repairing', interactionRange: 3, maxSlots: 8, hpPerSecond: 14, timberPerHp: 0.018, stonePerHp: 0.012 }),
  defend: Object.freeze({ label: 'Defending', leash: 150 }),
});

export const RALLY_KINDS = Object.freeze(['point', 'gather', 'build', 'repair', 'defend', 'attack']);
export const TACTICAL_ORDERS = Object.freeze(['hold', 'patrol', 'defend', 'attack-move', 'scout', 'garrison']);

const sharedUnits = {
  worker: { hp: 52, speed: 72, damage: 4, range: 24, cooldown: 1.05, sight: 190, trainTime: 8, pop: 1, capacity: 20, gatherRate: 8, buildAssist: 0.72, age: 1 },
  melee: { hp: 112, speed: 62, damage: 15, range: 30, cooldown: 0.92, sight: 215, trainTime: 12, pop: 2, age: 1 },
  ranged: { hp: 76, speed: 58, damage: 11, range: 155, cooldown: 1.18, sight: 245, trainTime: 15, pop: 2, age: 2 },
};

const sharedBuildings = {
  heart: { hp: 900, radius: 44, pop: 10, buildTime: 0, age: 1 },
  depot: { hp: 360, radius: 30, pop: 5, buildTime: 10, age: 1 },
  barracks: { hp: 520, radius: 36, pop: 0, buildTime: 14, age: 1 },
  tower: { hp: 420, radius: 25, pop: 0, buildTime: 12, damage: 13, range: 190, cooldown: 1.15, age: 2 },
};

export const GUARDIAN_PROFILES = Object.freeze({
  prowler: Object.freeze({ role: 'prowler', name: 'Shard Prowler', hp: 94, speed: 66, damage: 10, range: 28, cooldown: 1.05, sight: 205, radius: 12 }),
  sentinel: Object.freeze({ role: 'sentinel', name: 'Mossbound Sentinel', hp: 175, speed: 43, damage: 18, range: 34, cooldown: 1.28, sight: 190, radius: 15 }),
  channeler: Object.freeze({ role: 'channeler', name: 'Lumen Channeler', hp: 105, speed: 48, damage: 13, range: 135, cooldown: 1.42, sight: 235, radius: 13 }),
});

export const FAUNA_PROFILES = Object.freeze({
  reedhorn: Object.freeze({
    id: 'reedhorn', name: 'Reedhorn', faunaRole: 'fleet-herd', hp: 46, speed: 82, sight: 155, radius: 10,
    rations: 82, description: 'A swift tan-gray wetland grazer with reed-shaped antlers that flees hunters in coordinated herds.',
  }),
  'mossback-auroch': Object.freeze({
    id: 'mossback-auroch', name: 'Mossback Auroch', faunaRole: 'stampede-herd', hp: 175, speed: 55, sight: 175, radius: 17,
    damage: 23, range: 28, cooldown: 2.5, rations: 245,
    description: 'A rare dark, shaggy herd beast with mossy shoulders that turns on pursuers with a defensive horn charge.',
  }),
});

export const BUILDING_COSTS = Object.freeze({
  depot: { timber: 55, stone: 35 },
  barracks: { timber: 90, stone: 65 },
  tower: { timber: 50, stone: 80, lumen: 12 },
});

export const FACTIONS = Object.freeze({
  hearth: {
    id: 'hearth', name: 'Hearth Union', short: 'HEARTH', color: '#dc8b45', dark: '#6e2f25', glow: '#ffd79b',
    description: 'Adaptable frontier families. Strong food economy and balanced armies.',
    worker: 'Steward', melee: 'Warden', ranged: 'Slingwright',
    heart: 'Longhouse', depot: 'Storeyard', barracks: 'Muster Hall', tower: 'Watchpost',
    affinity: 'rations', gatherBonus: 1.28, buildSpeed: 1, buildingHp: 1, unitHp: 1, unitSpeed: 1,
    ageBonuses: { 2: { name: 'Provision Roads', gatherRate: 1.1, trainSpeed: 1.08 }, 3: { name: 'Banner Cohorts', unitHp: 1.12, unitDamage: 1.12 } },
    costs: { worker: { rations: 42 }, melee: { rations: 58, timber: 18 }, ranged: { rations: 52, timber: 30, lumen: 4 } },
  },
  grove: {
    id: 'grove', name: 'Grove Pact', short: 'GROVE', color: '#58b57b', dark: '#205844', glow: '#abf5bd',
    description: 'Root-and-spore cooperatives. Fast workers, living buildings, lighter armor.',
    worker: 'Tender', melee: 'Trailguard', ranged: 'Seedbow',
    heart: 'Heartgrove', depot: 'Rootcellar', barracks: 'Muster Canopy', tower: 'Thornpost',
    affinity: 'timber', gatherBonus: 1.3, buildSpeed: 1.25, buildingHp: 0.84, unitHp: 0.9, unitSpeed: 1.1,
    ageBonuses: { 2: { name: 'Rootways', unitSpeed: 1.08, buildSpeed: 1.12 }, 3: { name: 'Verdant Renewal', gatherRate: 1.18, repairRate: 1.2 } },
    costs: { worker: { rations: 38 }, melee: { rations: 50, timber: 15 }, ranged: { rations: 48, timber: 25, lumen: 6 } },
  },
  cairn: {
    id: 'cairn', name: 'Cairn Assembly', short: 'CAIRN', color: '#8eb4c9', dark: '#3d5266', glow: '#d9f3ff',
    description: 'Awakened stone communities. Durable, deliberate, and expensive to move.',
    worker: 'Mason', melee: 'Bulwark', ranged: 'Shardsinger',
    heart: 'First Cairn', depot: 'Stone Cache', barracks: 'Resonance Yard', tower: 'Beacon Stack',
    affinity: 'stone', gatherBonus: 1.34, buildSpeed: 0.9, buildingHp: 1.34, unitHp: 1.18, unitSpeed: 0.86,
    ageBonuses: { 2: { name: 'Resonant Masonry', buildingHp: 1.15, repairRate: 1.12 }, 3: { name: 'Awakened Monoliths', unitDamage: 1.18, unitHp: 1.08 } },
    costs: { worker: { rations: 45, stone: 4 }, melee: { rations: 62, stone: 24 }, ranged: { rations: 54, stone: 22, lumen: 7 } },
  },
  tideglass: {
    id: 'tideglass', name: 'Tideglass Compact', short: 'TIDEGLASS', color: '#35b9c6', dark: '#263756', glow: '#b6fff2',
    description: 'Coastal glasswright communes. Swift precision armies refract Lumen into range and control.',
    worker: 'Reefwright', melee: 'Tideguard', ranged: 'Prismcaster',
    heart: 'Glasswater Hall', depot: 'Tidevault', barracks: 'Prism Court', tower: 'Lens Spire',
    affinity: 'lumen', gatherBonus: 1.42, buildSpeed: 1.05, buildingHp: 0.9, unitHp: 0.96, unitSpeed: 1.04,
    roleDamage: { melee: 0.96, ranged: 1.18 }, roleRange: { ranged: 1.08 },
    ageBonuses: { 2: { name: 'Refracted Paths', captureRate: 1.22, unitRange: 1.06 }, 3: { name: 'Prismatic Accord', treasureBonus: 1.25, unitDamage: 1.12, trainSpeed: 1.12 } },
    costs: { worker: { rations: 40, lumen: 2 }, melee: { rations: 52, timber: 18, lumen: 3 }, ranged: { rations: 48, timber: 28, lumen: 9 } },
  },
  duskfen: {
    id: 'duskfen', name: 'Duskfen Covenant', short: 'DUSKFEN', color: '#9b78c9', dark: '#302442', glow: '#e4c6ff',
    description: 'Peat, reed, and lantern communities. Far-seeing controllers who weaken intruders in the mist.',
    worker: 'Bogwright', melee: 'Mirewarden', ranged: 'Lantern Hexer',
    heart: 'Fenlight Moot', depot: 'Peat Vault', barracks: 'Reed Court', tower: 'Wisp Spire',
    affinity: 'timber', gatherBonus: 1.22, buildSpeed: 1.08, buildingHp: 0.94, unitHp: 0.98, unitSpeed: 0.98,
    roleDamage: { ranged: 1.12 }, roleRange: { ranged: 1.1 },
    ageBonuses: { 2: { name: 'Lantern Paths', unitSight: 1.16, repairRate: 1.12 }, 3: { name: 'Covenant Mists', unitRange: 1.08, captureRate: 1.15 } },
    costs: { worker: { rations: 39, timber: 4 }, melee: { rations: 54, timber: 22 }, ranged: { rations: 46, timber: 34, lumen: 8 } },
  },
  skyclave: {
    id: 'skyclave', name: 'Skyclave Confluence', short: 'SKYCLAVE', color: '#e0b84f', dark: '#604030', glow: '#fff0a6',
    description: 'Highland wind, sail, and copper communes. Rapid engineers whose updraft defenses anchor a team.',
    worker: 'Windwright', melee: 'Aegis Lancer', ranged: 'Kitecaster',
    heart: 'Aerolith Hall', depot: 'Sailstore', barracks: 'Gale Bastion', tower: 'Copper Kite',
    affinity: 'stone', gatherBonus: 1.24, buildSpeed: 1.18, buildingHp: 1.08, unitHp: 0.94, unitSpeed: 1.06,
    roleDamage: { melee: 1.06 }, roleRange: { ranged: 1.05 },
    ageBonuses: { 2: { name: 'Copper Updrafts', buildSpeed: 1.1, trainSpeed: 1.08 }, 3: { name: 'Meridian Sails', buildingHp: 1.12, unitSpeed: 1.08 } },
    costs: { worker: { rations: 41, stone: 3 }, melee: { rations: 56, stone: 18 }, ranged: { rations: 50, timber: 24, stone: 12, lumen: 5 } },
  },
});

export const HEROES = Object.freeze({
  hearth: Object.freeze({
    id: 'mara-venn', name: 'Mara Venn', title: 'Banner Captain', unlockAge: 3,
    visual: 'Amber-red expedition cloak, brass sun-banner, broad frontier saber.',
    hp: 245, speed: 64, damage: 25, range: 38, cooldown: 0.82, sight: 270, pop: 4, trainTime: 28,
    cost: Object.freeze({ rations: 210, timber: 85, lumen: 24 }),
    ability: Object.freeze({ id: 'rallying-cry', name: 'Rallying Cry', cooldown: 18, range: 135, duration: 6 }),
  }),
  grove: Object.freeze({
    id: 'ilyra-mossstep', name: 'Ilyra Mossstep', title: 'Verdant Seer', unlockAge: 3,
    visual: 'Leaf mantle, luminous seedbow, curling root-staff and firefly motes.',
    hp: 205, speed: 70, damage: 21, range: 170, cooldown: 1.02, sight: 295, pop: 4, trainTime: 28,
    cost: Object.freeze({ rations: 185, timber: 120, lumen: 28 }),
    ability: Object.freeze({ id: 'verdant-renewal', name: 'Verdant Renewal', cooldown: 20, range: 145, heal: 30 }),
  }),
  cairn: Object.freeze({
    id: 'orun-bellstone', name: 'Orun Bellstone', title: 'Resonant Colossus', unlockAge: 3,
    visual: 'Slate body, cyan rune seams, monumental bell-hammer and stone mantle.',
    hp: 315, speed: 51, damage: 31, range: 42, cooldown: 1.12, sight: 255, pop: 4, trainTime: 30,
    cost: Object.freeze({ rations: 220, stone: 145, lumen: 22 }),
    ability: Object.freeze({ id: 'faultsong', name: 'Faultsong', cooldown: 22, range: 105, damage: 26 }),
  }),
  tideglass: Object.freeze({
    id: 'neris-prismwake', name: 'Neris Prismwake', title: 'Prism Navigator', unlockAge: 3,
    visual: 'Sea-blue glass lamellar, turquoise light-spear and rotating prism lens.',
    hp: 220, speed: 67, damage: 23, range: 185, cooldown: 0.96, sight: 305, pop: 4, trainTime: 27,
    cost: Object.freeze({ rations: 190, timber: 75, stone: 55, lumen: 34 }),
    ability: Object.freeze({ id: 'prism-volley', name: 'Prism Volley', cooldown: 19, range: 190, damage: 18, splash: 80 }),
  }),
  duskfen: Object.freeze({
    id: 'vesra-mirelight', name: 'Vesra Mirelight', title: 'Fen Warden', unlockAge: 3,
    visual: 'Peat-green mantle, violet fen lantern, reed crown and hooked marsh glaive.',
    hp: 225, speed: 61, damage: 22, range: 150, cooldown: 1.02, sight: 315, pop: 4, trainTime: 28,
    cost: Object.freeze({ rations: 195, timber: 105, lumen: 32 }),
    ability: Object.freeze({ id: 'mirelight-veil', name: 'Mirelight Veil', cooldown: 20, range: 155, damage: 8, duration: 6 }),
  }),
  skyclave: Object.freeze({
    id: 'talan-aerolith', name: 'Talan Aerolith', title: 'Gale Architect', unlockAge: 3,
    visual: 'White sailcloth coat, copper wind vanes, amber kite-shield and drafting spear.',
    hp: 235, speed: 68, damage: 24, range: 95, cooldown: 0.92, sight: 290, pop: 4, trainTime: 27,
    cost: Object.freeze({ rations: 205, timber: 80, stone: 90, lumen: 28 }),
    ability: Object.freeze({ id: 'updraft-bulwark', name: 'Updraft Bulwark', cooldown: 21, range: 145, armor: 4, duration: 7 }),
  }),
});

export function ageModifiers(factionId, age = 1) {
  const faction = FACTIONS[factionId];
  const result = { unitHp: 1, unitDamage: 1, unitSpeed: 1, unitRange: 1, unitSight: 1, buildingHp: 1, gatherRate: 1, buildSpeed: 1, repairRate: 1, trainSpeed: 1, captureRate: 1, treasureBonus: 1 };
  for (let tier = 2; tier <= Math.min(3, age); tier++) {
    for (const [key, value] of Object.entries(faction?.ageBonuses?.[tier] || {})) if (typeof value === 'number' && result[key] !== undefined) result[key] *= value;
  }
  return result;
}

export function unitProfile(factionId, role, age = 1) {
  const faction = FACTIONS[factionId], base = role === 'hero' ? HEROES[factionId] : sharedUnits[role], modifiers = ageModifiers(factionId, age);
  if (!faction || !base) return null;
  return {
    ...base,
    hp: Math.round(base.hp * faction.unitHp * modifiers.unitHp),
    speed: base.speed * faction.unitSpeed * modifiers.unitSpeed,
    damage: base.damage * (faction.roleDamage?.[role] || 1) * modifiers.unitDamage,
    range: base.range * (faction.roleRange?.[role] || 1) * modifiers.unitRange,
    sight: base.sight * modifiers.unitSight,
    gatherRate: (base.gatherRate || 0) * modifiers.gatherRate,
    trainTime: base.trainTime / modifiers.trainSpeed,
  };
}

export function buildingProfile(factionId, type, age = 1) {
  const faction = FACTIONS[factionId], base = sharedBuildings[type], modifiers = ageModifiers(factionId, age);
  return { ...base, hp: Math.round(base.hp * faction.buildingHp * modifiers.buildingHp) };
}

export function unlockAge(kind, type) {
  if (kind === 'unit' && type === 'hero') return 3;
  return (kind === 'unit' ? sharedUnits[type] : sharedBuildings[type])?.age || 1;
}

export function labelFor(factionId, role) {
  if (role === 'hero') return HEROES[factionId]?.name || role;
  return FACTIONS[factionId]?.[role] || role;
}

export function canAfford(stock, cost) {
  return Object.entries(cost).every(([key, amount]) => (stock[key] || 0) >= amount);
}

export function spend(stock, cost) {
  for (const [key, amount] of Object.entries(cost)) stock[key] -= amount;
}
